import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Icon,
  Input,
  PasswordInput,
  Text,
} from "@salestrak/ui";
import { useRouter } from "#app/lib/hooks/router-compat";
import { useAuth } from "#app/lib/auth-context";
import { PASSWORD_MIN_LENGTH, newPasswordProblem } from "#app/lib/auth-service";
import { useAnalytics, AnalyticsContext, AuthEvent } from "#app/lib/analytics";
import { useSplashAutoFocus } from "#app/lib/splash-context";
import { validateEmail } from "#app/lib/custom-field-utils";
import { GoogleSignInButton } from "./google-sign-in-button";

export function RegisterForm() {
  const { signUp } = useAuth();
  const { track } = useAnalytics();
  const router = useRouter();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  // Field errors sit under their own field; a rejected submit reads in the
  // form-level Alert above the CTA.
  const [nameError, setNameError] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [confirmError, setConfirmError] = React.useState("");
  const [formError, setFormError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [googlePending, setGooglePending] = React.useState(false);
  const nameRef = useSplashAutoFocus();

  const busy = submitting || googlePending;

  const handleRegister = async () => {
    if (busy) return;
    setFormError("");
    if (!name.trim()) {
      setNameError("Full name is required.");
      return;
    }
    if (!email.trim()) {
      setEmailError("Email is required.");
      return;
    }
    // The form is noValidate (the native bubble clashes with the inline
    // errors), so the format check lives here.
    if (!validateEmail(email.trim())) {
      setEmailError("Enter a valid email address.");
      return;
    }
    if (!password.trim()) {
      setPasswordError("Password is required.");
      return;
    }
    const problem = newPasswordProblem(password);
    if (problem) {
      setPasswordError(problem);
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await signUp(name.trim(), email.trim(), password);
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignUp, label: "success" });
      // The account starts email-unverified; sign-in happens through the
      // emailed verification link, so hand off to the check-your-email screen.
      router.replace({
        pathname: "/(auth)/verify-email-sent",
        params: { email: email.trim() },
      });
    } catch (err: any) {
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignUp, label: "failure" });
      setFormError(err.message ?? "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // The form owns submission — Enter from any field registers.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleRegister();
  };

  return (
    <Box as="form" noValidate direction="column" gap="lg" onSubmit={handleSubmit}>
      <GoogleSignInButton
        onError={setFormError}
        disabled={submitting}
        onPendingChange={setGooglePending}
      />

      <Box gap="base">
        <Text
          as="label"
          htmlFor="register-name"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Full Name
        </Text>
        <Box gap="sm">
          <Input
            id="register-name"
            size="xl"
            placeholder="John Smith"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError("");
            }}
            aria-invalid={!!nameError}
            ref={nameRef}
            autoComplete="name"
            disabled={busy}
          />
          {nameError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {nameError}
            </Text>
          ) : null}
        </Box>
      </Box>

      <Box gap="base">
        <Text
          as="label"
          htmlFor="register-email"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Email
        </Text>
        <Box gap="sm">
          <Input
            id="register-email"
            size="xl"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
            }}
            aria-invalid={!!emailError}
            autoComplete="email"
            disabled={busy}
          />
          {emailError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {emailError}
            </Text>
          ) : null}
        </Box>
      </Box>

      <Box gap="base">
        <Text
          as="label"
          htmlFor="register-password"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Password
        </Text>
        <Box gap="sm">
          <PasswordInput
            id="register-password"
            size="xl"
            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError("");
              setConfirmError("");
            }}
            aria-invalid={!!passwordError}
            autoComplete="new-password"
            disabled={busy}
          />
          {passwordError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {passwordError}
            </Text>
          ) : null}
        </Box>
      </Box>

      <Box gap="base">
        <Text
          as="label"
          htmlFor="register-confirm"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Confirm Password
        </Text>
        <Box gap="sm">
          <PasswordInput
            id="register-confirm"
            size="xl"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmError("");
            }}
            aria-invalid={!!confirmError}
            autoComplete="new-password"
            disabled={busy}
          />
          {confirmError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {confirmError}
            </Text>
          ) : null}
        </Box>
      </Box>

      {formError ? (
        <Alert variant="error">
          <Icon name="alert-circle" />
          <AlertTitle>{formError}</AlertTitle>
        </Alert>
      ) : null}

      <Box direction="column" paddingTop="lg">
        {/* Primary CTA, enabled while fields are empty: the handler owns the
            required-field and password checks and reads them inline. */}
        <Button
          type="submit"
          size="xl"
          loading={submitting}
          disabled={googlePending}
        >
          Sign up
        </Button>
      </Box>
    </Box>
  );
}
